import React, { useRef, useState } from 'react'
import Skeleton from 'react-loading-skeleton'
import 'react-loading-skeleton/dist/skeleton.css'
import { useTheme } from 'styled-components'
import { Heart as DisLike, HeartFill as Like } from 'react-bootstrap-icons'
import {
  Button,
  Alert,
  shape,
  useIntersectionObserver,
  Modal,
  LoginForm,
  SignUpForm,
  ForgotPasswordForm
} from '~ui'
import {
  CardContainer,
  CardInfo,
  WrapLogo,
  WrapTags,
  CardLogo,
  SoldOut,
  PriceWrapper,
  RibbonBox,
  TitleWrapper,
  LastOrder,
  GuestUserLink
} from './styles'

import { nanoid } from 'nanoid'

import { useLanguage, useConfig, useOrder, useUtils, useSession, useApi, SingleProductCard as SingleProductCardController } from '~components'

const NEON_PRODUCT_STYLES = [
  { background: '#ff0090', text: '#FFF' },
  { background: '#fff300', text: '#000' },
  { background: '#0056ff', text: '#FFF' }
]

const normalizeHex = (hex = '') => {
  const cleaned = hex.replace('#', '').trim()
  if (cleaned.length === 3) {
    return cleaned.split('').map(char => char + char).join('')
  }
  return cleaned
}

const isLightHex = (hex) => {
  const normalized = normalizeHex(hex)
  if (!/^[0-9A-Fa-f]{6}$/.test(normalized)) return false
  const r = parseInt(normalized.slice(0, 2), 16)
  const g = parseInt(normalized.slice(2, 4), 16)
  const b = parseInt(normalized.slice(4, 6), 16)
  return (r * 299 + g * 587 + b * 114) / 1000 > 150
}

const getNeonCardColors = (index, overrideColor) => {
  if (overrideColor) {
    return {
      background: overrideColor,
      text: isLightHex(overrideColor) ? '#000' : '#FFF'
    }
  }
  return NEON_PRODUCT_STYLES[index % NEON_PRODUCT_STYLES.length]
}

const singleProductCardPropsAreEqual = (prevProps, nextProps) => {
  return JSON.stringify(prevProps.product) === JSON.stringify(nextProps.product) &&
    prevProps.isSkeleton === nextProps.isSkeleton &&
    prevProps.isSoldOut === nextProps.isSoldOut &&
    prevProps.isCartOnProductsList === nextProps.isCartOnProductsList &&
    prevProps.productAddedToCartLength === nextProps.productAddedToCartLength
}

const SingleProductCardUI = React.memo((props) => {
  const {
    product,
    isSoldOut,
    isSkeleton,
    onProductClick,
    isCartOnProductsList,
    useCustomFunctionality,
    onCustomClick,
    customText,
    useKioskApp,
    handleFavoriteProduct,
    isFavorite,
    isPreviously,
    viewString,
    isCustomerMode,
    index
  } = props

  const [, t] = useLanguage()
  const [$element, isObserved] = useIntersectionObserver()
  const [stateConfig] = useConfig()
  const [{ parsePrice, optimizeImage, parseDate }] = useUtils()
  const [orderState, { addProduct, updateProduct }] = useOrder()
  const [{ auth }, { login }] = useSession()
  const [ordering] = useApi()
  const [actionStatus, setActionStatus] = useState({ loading: false, error: null })
  const theme = useTheme()
  const favoriteRef = useRef(null)

  const businessId = product?.business_id || props.businessId
  const currentCart = orderState.carts?.[`businessId:${businessId}`]
  const productsLength = currentCart?.products?.reduce((productsLength, cartProduct) => {
    return productsLength + (cartProduct.id === product?.id ? cartProduct.quantity : 0)
  }, 0) || 0

  const [isModalOpen, setIsModalOpen] = useState(false)
  const [modalPageToShow, setModalPageToShow] = useState(null)
  const [alertState, setAlertState] = useState({ open: false, content: [] })

  const editMode = typeof product?.code !== 'undefined'
  const isObservedValidation = isObserved || useKioskApp

  const removeToBalance = editMode ? product?.quantity : 0

  const cartProducts = Object.values(orderState.carts).reduce((products, _cart) => [...products, ..._cart?.products], [])
  const productBalance = cartProducts.reduce((sum, _product) => sum + (_product.id === product?.id ? _product.quantity : 0), 0)

  const totalBalance = (productBalance || 0) - removeToBalance

  const maxCartProductConfig = (stateConfig.configs.max_product_amount ? parseInt(stateConfig.configs.max_product_amount) : 100) - totalBalance

  const hideAddButton = false // theme?.business_view?.components?.products?.components?.add_to_cart_button?.hidden ?? false
  const hideProductDescription = theme?.business_view?.components?.products?.components?.product?.components?.description?.hidden
  const hideProductDummyLogo = theme?.business_view?.components?.products?.components?.product?.components?.dummy?.hidden
  const hideProductLogo = viewString
    ? theme?.[viewString]?.components?.cart?.components?.products?.image?.hidden
    : theme?.business_view?.components?.products?.components?.product?.components?.image?.hidden
  const hideFavoriteIcon = theme?.business_view?.components?.products?.components?.product?.components?.favorite?.hidden

  // const productsRows = theme?.layouts?.business_view?.components?.products?.components?.layout?.rows

  let maxCartProductInventory = (product?.inventoried ? product?.quantity : undefined) - totalBalance
  maxCartProductInventory = !isNaN(maxCartProductInventory) ? maxCartProductInventory : maxCartProductConfig

  const maxProductQuantity = Math.min(maxCartProductConfig, maxCartProductInventory)

  const handleIncrement = (e) => {
    e && e.stopPropagation()
    if (!auth) {
      setModalPageToShow('login')
      setIsModalOpen(true)
      return
    }
    const cartData = (currentCart && currentCart?.uuid) ? currentCart : { business_id: businessId }
    if (productsLength > 0) {
      const cartProduct = currentCart?.products?.find(p => p.id === product?.id)
      if (cartProduct) {
        updateProduct({
          id: product.id,
          code: cartProduct.code,
          quantity: cartProduct.quantity + 1
        }, cartData)
      }
    } else {
      addProduct({
        ...product,
        quantity: 1,
        business_id: businessId,
        business_slug: product?.business?.slug || props.businessSlug
      }, cartData)
    }
  }

  const handleDecrement = (e) => {
    e && e.stopPropagation()
    if (productsLength > 0) {
      const cartProduct = currentCart?.products?.find(p => p.id === product?.id)
      if (cartProduct) {
        updateProduct({
          id: product.id,
          code: cartProduct.code,
          quantity: cartProduct.quantity - 1
        }, currentCart)
      }
    }
  }

  const handleClickProduct = (e) => {
    if (favoriteRef?.current?.contains(e.target)) return
    if (!auth) {
      setModalPageToShow('login')
      setIsModalOpen(true)
      return
    }
    if (productsLength && product?.maximum_per_order && productsLength >= product?.maximum_per_order) {
      setAlertState({
        open: true,
        content: [t('PRODUCT_ON_MAXIMUM_ORDER', 'The product is on maximum order')]
      })
      return
    }

    if (isFavorite) {
      onProductClick && onProductClick(product)
      return
    }

    if (!isSkeleton && !useCustomFunctionality && onProductClick) {
      // If product has extras or is not in cart, open modal.
      // User requested "add button" and functionality, but usually items with options need modal.
      // However, for standard items, we can just increment.
      if (product.extras?.length > 0) {
        onProductClick(product, product?.business?.slug)
      } else {
        handleIncrement(e)
      }
    } else if (useCustomFunctionality && onCustomClick) {
      onCustomClick(product)
    }
  }

  const closeAlert = () => {
    setAlertState({
      open: false,
      content: []
    })
  }

  const handleChangeFavorite = () => {
    if (auth) {
      handleFavoriteProduct && handleFavoriteProduct(!product?.favorite)
    } else {
      setModalPageToShow('login')
      setIsModalOpen(true)
    }
  }

  const closeAuthModal = () => {
    setIsModalOpen(false)
    setModalPageToShow(null)
  }

  const handleCreateGuestUser = async (values) => {
    try {
      setActionStatus({ ...actionStatus, loading: true })
      const { content: { error, result } } = await ordering.users().save(values)
      if (!error) {
        setActionStatus({ error: null, loading: false })
        login({
          user: result,
          token: result.session?.access_token
        })
        closeAuthModal()
      } else {
        setActionStatus({ error: result, loading: false })
      }
    } catch (err) {
      setActionStatus({ error: err.message, loading: false })
    }
  }
  const handleUpdateGuest = () => {
    const guestToken = nanoid()
    if (guestToken) handleCreateGuestUser({ guest_token: guestToken })
  }

  const handleSuccessLogin = (user) => {
    if (user) {
      closeAuthModal()
    }
  }

  const handleCustomModalClick = (e, { page }) => {
    e.preventDefault()
    setModalPageToShow(page)
  }

  const handleSuccessSignup = (user) => {
    login({
      user,
      token: user?.session?.access_token
    })
  }

  const { background: bgColor } = getNeonCardColors(index, props.neonBorderColor)
  const borderColor = props.neonBorderColor || bgColor
  const textColor = '#000'
  return (
    <>
      <CardContainer
        ref={$element}
        soldOut={isSoldOut || maxProductQuantity <= 0}
        onClick={handleClickProduct}
        isCartOnProductsList={isCartOnProductsList}
        className='product-card'
        isShowAddButt={!useCustomFunctionality && !hideAddButton && !isSkeleton}
        useCustomFunctionality={useCustomFunctionality}
        borderColor={borderColor}
        bgColor={bgColor}
        textColor={textColor}
      >
        {isObservedValidation && (
          <div className='product-content'>
            {!useCustomFunctionality && (
              <>
                {!isSkeleton
                  ? (<WrapLogo
                    isBgimage={optimizeImage(!hideProductLogo ? product?.images || theme?.images?.dummies?.product : '', 'h_200,c_limit')}
                  >
                    {product?.ribbon?.enabled && (
                      <RibbonBox
                        bgColor={product?.ribbon?.color}
                        isRoundRect={product?.ribbon?.shape === shape?.rectangleRound}
                        isCapsule={product?.ribbon?.shape === shape?.capsuleShape}
                      >
                        {product?.ribbon?.text}
                      </RibbonBox>
                    )}
                    <CardLogo
                      className='image'
                      soldOut={isSoldOut || maxProductQuantity <= 0}
                      bgimage={optimizeImage(product?.images || (!hideProductDummyLogo && theme?.images?.dummies?.product), 'h_200,c_limit')}
                    />
                  </WrapLogo>)
                  : <Skeleton height={150} width={'100%'} />}

                <CardInfo
                  soldOut={isSoldOut || maxProductQuantity <= 0}
                  isBgimage={optimizeImage(product?.images || theme?.images?.dummies?.product, 'h_86,c_limit')}
                  oneLine={isPreviously}
                  textColor={textColor}
                >
                  <TitleWrapper>
                    {!isSkeleton
                      ? <h1>{product?.name}</h1>
                      : <Skeleton width={100} />}
                    {(!useKioskApp && !isPreviously && isCustomerMode && !hideFavoriteIcon) && (
                      !isSkeleton
                        ? (
                          <span onClick={() => handleChangeFavorite()} ref={favoriteRef}>
                            {product?.favorite ? <Like /> : <DisLike />}
                          </span>
                          )
                        : (<Skeleton width={16} height={16} />)
                    )}
                  </TitleWrapper>
                  {!hideProductDescription && (
                    <>
                      {!isSkeleton ? (<p className="short-desc">{product?.description || 'Short description'}</p>) : (<Skeleton width={100} />)}
                    </>
                  )}
                  {isPreviously && (!isSkeleton ? (<LastOrder>{t('LAST_ORDERED_ON', 'Last ordered on')} {parseDate(product?.last_ordered_date, { outputFormat: 'MMM DD, YYYY' })}</LastOrder>) : (<Skeleton width={80} />))}
                </CardInfo>

                <div className='price-add-wrapper'>
                  {!isSkeleton
                    ? (<PriceWrapper isOffPrice={product?.offer_price && product?.in_offer} textColor={textColor}>
                      <span className='current-price'>{product?.price ? parsePrice(product?.price) : ''}</span>
                      {!(isSoldOut || maxProductQuantity <= 0) && (
                        <span className='off-price'>{product?.offer_price && product?.in_offer ? parsePrice(product?.offer_price) : ''}</span>
                      )}
                      {product?.tags && product?.tags.length > 0 && (
                        <WrapTags>
                          {product?.tags.map((tag, i) => (
                            <div key={i}>
                              <img src={tag.image ?? ''}></img>
                            </div>
                          ))}
                        </WrapTags>
                      )}
                    </PriceWrapper>)
                    : <Skeleton width={50} />}

                  {!useCustomFunctionality && !hideAddButton && !isSkeleton && (
                    productsLength > 0
                      ? (
                        <div className='qty-controls'>
                          <span className='control' onClick={handleIncrement}>+</span>
                          <span className='qty'>{productsLength}</span>
                          <span className='control' onClick={handleDecrement}>-</span>
                        </div>
                        )
                      : (
                        <Button className='qty-controls' borderRight={30} style={{ backgroundColor: '#fff', width: '45%', borderRight: 30, justifyContent: 'center' }} onClick={handleIncrement} disabled={productsLength && product?.maximum_per_order && productsLength >= product?.maximum_per_order}>
                          {t('ADD', 'Add')}
                        </Button>
                        )
                  )}
                </div>
                {(isSoldOut || maxProductQuantity <= 0) && (
                  <SoldOut isBottom={product?.ribbon?.enabled}>{t('SOLD_OUT', 'Sold out')}</SoldOut>
                )}
              </>
            )}
            {useCustomFunctionality && customText && (
              <span style={{ fontSize: 16, fontWeight: 500 }}>{customText}</span>
            )}
          </div>
        )}

      </CardContainer>
      {isModalOpen && (
        <Modal
          open={isModalOpen}
          onRemove={() => closeAuthModal()}
          onClose={() => closeAuthModal()}
          width='50%'
          authModal
        >
          {modalPageToShow === 'login' && (
            <LoginForm
              handleSuccessLogin={handleSuccessLogin}
              elementLinkToSignup={
                <a
                  onClick={
                    (e) => handleCustomModalClick(e, { page: 'signup' })
                  } href='#'
                >{t('CREATE_ACCOUNT', theme?.defaultLanguages?.CREATE_ACCOUNT || 'Create account')}
                </a>
              }
              elementLinkToForgotPassword={
                <a
                  onClick={
                    (e) => handleCustomModalClick(e, { page: 'forgotpassword' })
                  } href='#'
                >{t('RESET_PASSWORD', theme?.defaultLanguages?.RESET_PASSWORD || 'Reset Password')}
                </a>
              }
              useLoginByCellphone
              isPopup
              elementLinkToGuest={
                <GuestUserLink onClick={handleUpdateGuest}>
                  {actionStatus?.loading ? <Skeleton height={25} width={70} /> : t('AS_GUEST_USER', 'As guest user')}
                </GuestUserLink>
              }
            />
          )}
          {modalPageToShow === 'signup' && (
            <SignUpForm
              elementLinkToLogin={
                <a
                  onClick={
                    (e) => handleCustomModalClick(e, { page: 'login' })
                  } href='#'
                >{t('LOGIN', theme?.defaultLanguages?.LOGIN || 'Login')}
                </a>
              }
              useLoginByCellphone
              useChekoutFileds
              handleSuccessSignup={handleSuccessSignup}
              isPopup
              closeModal={() => closeAuthModal()}
            />
          )}
          {modalPageToShow === 'forgotpassword' && (
            <ForgotPasswordForm
              elementLinkToLogin={
                <a
                  onClick={
                    (e) => handleCustomModalClick(e, { page: 'login' })
                  } href='#'
                >{t('LOGIN', theme?.defaultLanguages?.LOGIN || 'Login')}
                </a>
              }
              isPopup
            />
          )}
        </Modal>
      )}
      <Alert
        title={t('PRODUCT', 'Product')}
        content={alertState.content}
        acceptText={t('ACCEPT', 'Accept')}
        open={alertState.open}
        onClose={() => closeAlert()}
        onAccept={() => closeAlert()}
        closeOnBackdrop={false}
      />
    </>
  )
}, singleProductCardPropsAreEqual)

export const SingleProductCard = (props) => {
  const singleProductCardProps = {
    ...props,
    UIComponent: SingleProductCardUI
  }
  return <SingleProductCardController {...singleProductCardProps} />
}
